/**
 * Opens (or creates) the IndexedDB database named 'IndexDB' (version 2) and returns a Promise
 * that resolves to the database connection object. If the database doesn't exist or a new version
 * is detected, the `onupgradeneeded` event fires, creating both 'pendingUploads' and 'pendingSubmissions'
 * object stores if necessary.
 *
 * @returns {Promise<IDBDatabase>} A promise that resolves with the opened database object.
 */
export function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('IndexDB', 1);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains('pendingUploads')) {
        db.createObjectStore('pendingUploads', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('pendingSubmissions')) {
        db.createObjectStore('pendingSubmissions', { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Adds a new record to the specified object store in the IndexedDB database.
 *
 * @async
 * @function addUploadItem
 * @param {Object} data - The data object to add.
 * @param {string} [storeName='pendingUploads'] - The object store to use.
 * @returns {Promise<number>} A promise that resolves to the new record's key.
 */
export async function addUploadItem(data, storeName = 'pendingUploads') {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.add(data);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}


// /**
//  * Opens (or creates) the IndexedDB database named 'IndexDB' (version 1) and returns a Promise 
//  * that resolves to the database connection object. If the database doesn't exist or the version 
//  * is newer, the `onupgradeneeded` event fires, creating the 'uploads' object store if necessary.
//  *
//  * @returns {Promise<IDBDatabase>} A promise that resolves with the opened database object. Necessary to perform further functions.
//  */
// export function openDatabase() {
//     return new Promise((resolve, reject) => {
//       // Attempt to open the database named 'IndexDB' at version 1
//       const request = indexedDB.open('IndexDB', 1);
//       request.onupgradeneeded = (event) => {
//         const db = request.result;
//         // If the 'uploads' store doesn't exist, create it with an auto-incrementing 'id' key
//         if (!db.objectStoreNames.contains('pendingUploads')) {
//           db.createObjectStore('pendingUploads', { keyPath: 'id', autoIncrement: true });
//         }
//       };
  
//       // on success: resolve the database object
//       request.onsuccess = () => {
//         resolve(request.result);
//       };
  
//       // on error: reject with the encountered error
//       request.onerror = () => {
//         reject(request.error);
//       };
//     });
//   }

// /**
//  * Adds a new upload item (a record) to the 'uploads' object store in the IndexedDB database.
//  * 
//  * @async
//  * @function addUploadItem
//  * @param {Object} data - The data object to add to the store. For example: { id, url, options } 
//  * @returns {Promise<number>} - A promise that resolves to the auto-incremented primary key (id) of the new record.
//  */
// export async function addUploadItem(data) {
//   // Open the database or use the existing one
//   const db = await openDatabase();
//   return new Promise((resolve, reject) => {
//     // Create a readwrite transaction 
//     const transaction = db.transaction('pendingUploads', 'readwrite');
//     const store = transaction.objectStore('pendingUploads');

//     // store.add() will fail if a record with the same key already exists
//     // store.put() could be used to overwrite existing records with the same key
//     const request = store.add(data);

//     // If successful, request.result is the new record's key
//     request.onsuccess = () => resolve(request.result);
//     request.onerror = () => reject(request.error);
//   });
// }

// /**
//  * Retrieves all records from the 'uploads' object store. 
//  *
//  * @async
//  * @function getAllUploads
//  * @returns {Promise<Object[]>} A promise that resolves to an array of all upload records lile [{}, {}, {}]
//  */
// export async function getAllUploads() {
//   const db = await openDatabase();
//   return new Promise((resolve, reject) => {
//     // 'readonly' transaction because we're only fetching data
//     const transaction = db.transaction('pendingUploads', 'readonly');
//     const store = transaction.objectStore('pendingUploads');

//     // getAll() fetches all records in this object store
//     const request = store.getAll();
//     request.onsuccess = () => {
//       resolve(request.result);
//     };
//     request.onerror = () => {
//       reject(request.error);
//     };
//   });
// }

// /**
//  * Deletes a specific record (by id) from the 'uploads' object store.
//  *
//  * @async
//  * @function deleteUploadItem
//  * @param {number} id - The primary key (id) of the record to delete.
//  * @returns {Promise<void>} A promise that resolves when the record is deleted.
//  */
// export async function deleteUploadItem(id) {
//   const db = await openDatabase();
//   return new Promise((resolve, reject) => {
//     const transaction = db.transaction('pendingUploads', 'readwrite');
//     const store = transaction.objectStore('pendingUploads');

//     // remove the record with the given id from the store
//     const request = store.delete(id);
//     request.onsuccess = () => resolve();
//     request.onerror = () => reject(request.error);
//   });
// }
